import React, { useState, useRef, useEffect } from "react";
import { Search, X } from "lucide-react";
import { useDebounce } from "../../utils/debounce";
import { formatJobRevNumber } from "../../utils/projectFormatting";

const DiscrepancySearch = ({
  discrepancies = [],
  onDiscrepancySelect,
  getStatusColor,
  getTypeColor,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredResults, setFilteredResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search function
  const debouncedSearch = useDebounce((term) => {
    if (!term || term.trim().length === 0) {
      setFilteredResults([]);
      setIsOpen(false);
      return;
    }

    const searchLower = term.toLowerCase();

    // Search only by project name, job number, and client name (NOT by type)
    const filtered = discrepancies.filter((disc) => {
      const projectName = (disc.project?.projectName || "").toLowerCase();
      const jobNumber = (disc.project?.jobNumber || "").toLowerCase();
      const clientName = (
        disc.project?.clientName ||
        disc.project?.client?.name ||
        ""
      ).toLowerCase();

      return (
        projectName.includes(searchLower) ||
        jobNumber.includes(searchLower) ||
        clientName.includes(searchLower)
      );
    });

    // Limit to 15 results
    const limitedResults = filtered.slice(0, 15);
    setFilteredResults(limitedResults);
    setIsOpen(limitedResults.length > 0);
  }, 300);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedIndex(-1);
    debouncedSearch(value);
  };

  // Handle discrepancy click
  const handleDiscrepancyClick = (discrepancy) => {
    if (onDiscrepancySelect) {
      onDiscrepancySelect(discrepancy);
    }
    clearSearch();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen || filteredResults.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredResults.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredResults.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredResults.length) {
          handleDiscrepancyClick(filteredResults[selectedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setSelectedIndex(-1);
        searchInputRef.current?.blur();
        break;
      default:
        break;
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setFilteredResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    searchInputRef.current?.focus();
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !searchInputRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Highlight search term in text
  const highlightText = (text, term) => {
    if (!text || !term) return text;

    const parts = text.split(new RegExp(`(${term})`, "gi"));
    return parts.map((part, index) =>
      part.toLowerCase() === term.toLowerCase() ? (
        <mark
          key={index}
          style={{
            background: 'rgba(251, 191, 36, 0.2)',
            color: 'inherit',
            fontWeight: '600',
            padding: '0 1px',
            borderRadius: '2px',
          }}
        >
          {part}
        </mark>
      ) : (
        part
      ),
    );
  };

  // Get type color (fallback if not provided)
  const getDiscrepancyTypeColor = (type) => {
    if (getTypeColor) return getTypeColor(type);

    const typeColors = {
      RFI: "#3b82f6",
      Clarification: "#10b981",
      Discrepancy: "#f59e0b",
      "Change Order": "#ef4444",
      General: "#6b7280",
    };
    return typeColors[type] || "#6b7280";
  };

  // Get status color (fallback if not provided)
  const getDiscrepancyStatusColor = (status) => {
    if (getStatusColor) return getStatusColor(status);

    const statusColors = {
      Draft: "#6b7280",
      "Waiting to Review": "#f59e0b",
      "Reviewed - not sent": "#3b82f6",
      Sent: "#10b981",
      Resolved: "#059669",
    };
    return statusColors[status] || "#6b7280";
  };

  return (
    <div style={{
      position: 'relative',
      width: '100%',
    }}>
      <div style={{ width: '100%' }}>
        <div style={{ position: 'relative', width: '100%' }}>
          <Search
            size={18}
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--gray-400)',
              pointerEvents: 'none',
            }}
          />
          <input
            ref={searchInputRef}
            type="text"
            style={{
              width: '100%',
              padding: '10px 40px',
              paddingRight: '40px',
              border: '1px solid var(--gray-600)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--dark-surface)',
              color: 'var(--gray-50)',
              fontSize: 'var(--text-sm)',
              transition: 'all 0.2s ease',
            }}
            placeholder="Search by project name, job number, or client..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = 'var(--cta-blue)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = 'var(--gray-600)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              aria-label="Clear search"
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '28px',
                height: '28px',
                padding: 0,
                border: 'none',
                background: 'transparent',
                color: 'var(--gray-400)',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'var(--dark-elevated)';
                e.currentTarget.style.color = 'var(--gray-50)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--gray-400)';
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            background: 'var(--dark-surface)',
            border: '1px solid var(--gray-600)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-xl)',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1000,
            animation: 'slideDown 0.2s ease',
          }}
        >
          <style>
            {`
              @keyframes slideDown {
                from {
                  opacity: 0;
                  transform: translateY(-10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
            `}
          </style>
          {filteredResults.length === 0 ? (
            <div style={{
              padding: 'var(--space-4)',
              textAlign: 'center',
              color: 'var(--gray-400)',
              fontSize: 'var(--text-sm)',
            }}>
              No discrepancies found for "{searchTerm}"
            </div>
          ) : (
            <>
              <div style={{ padding: 'var(--space-2)' }}>
                {filteredResults.map((discrepancy, index) => (
                  <div
                    key={discrepancy.id}
                    onClick={() => handleDiscrepancyClick(discrepancy)}
                    onMouseEnter={() => setSelectedIndex(index)}
                    style={{
                      padding: '10px 12px',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      transition: 'background 0.15s ease',
                      border: '1px solid transparent',
                      ...(selectedIndex === index && {
                        background: 'var(--dark-elevated)',
                        borderColor: 'var(--cta-blue)',
                      }),
                    }}
                    onMouseOver={(e) => {
                      if (selectedIndex !== index) {
                        e.currentTarget.style.background = 'var(--dark-elevated)';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (selectedIndex !== index) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}>
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                          marginTop: '2px',
                          backgroundColor: getDiscrepancyTypeColor(
                            discrepancy.discrepancyType,
                          ),
                          color: 'white',
                        }}
                      >
                        {discrepancy.discrepancyType || "General"}
                      </span>
                      <div style={{
                        flex: 1,
                        minWidth: 0,
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-medium)',
                          color: 'var(--gray-50)',
                          lineHeight: 1.4,
                        }}>
                          {discrepancy.project && (
                            <>
                              <span>
                                {highlightText(
                                  discrepancy.project.projectName,
                                  searchTerm,
                                )}
                              </span>
                              {discrepancy.project.jobNumber && (
                                <>
                                  {" - "}
                                  <span style={{
                                    color: 'var(--gray-400)',
                                    fontWeight: 'var(--font-normal)',
                                  }}>
                                    {highlightText(
                                      formatJobRevNumber(
                                        discrepancy.project.jobNumber,
                                        discrepancy.project.rev,
                                      ),
                                      searchTerm,
                                    )}
                                  </span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginTop: '2px',
                          fontSize: '13px',
                          color: 'var(--gray-400)',
                        }}>
                          {(discrepancy.project?.clientName ||
                            discrepancy.project?.client?.name) && (
                            <span>
                              {highlightText(
                                discrepancy.project.clientName ||
                                  discrepancy.project.client?.name,
                                searchTerm,
                              )}
                            </span>
                          )}
                          {discrepancy.discrepancyStatus && (
                            <>
                              {(discrepancy.project?.clientName ||
                                discrepancy.project?.client?.name) &&
                                " • "}
                              <span
                                style={{
                                  fontWeight: 'var(--font-medium)',
                                  color: getDiscrepancyStatusColor(
                                    discrepancy.discrepancyStatus,
                                  ),
                                }}
                              >
                                {discrepancy.discrepancyStatus}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredResults.length === 15 && (
                <div style={{
                  padding: '8px 16px',
                  textAlign: 'center',
                  color: 'var(--gray-500)',
                  fontSize: '12px',
                  borderTop: '1px solid var(--gray-700)',
                }}>
                  More results available - refine your search
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscrepancySearch;
